import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, isObservable, of } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ValidatorFn, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { SearchCustomersState } from './../../store/search-customers/search-customers.reducer';
import * as fromSearchCustomersActions from './../../store/search-customers/search-customers.actions';
import { PendingSearchInput, SearchInputFilterType } from './../../store/search-customers/search-customers.actions';
import * as fromSearchCustomerSelectors from './../../store/search-customers/search-customers.selectors';
import { ICustomer } from '@signal/asp-data-commons';
import { VehicleService } from '../../shared/services/vehicle.service';
import { SubscriptionList } from '../../shared/models/asp.types';
import { unsubscribeSubscriptions, getLicenseToVinEnabled, DirtyErrorStateMatcher } from '../../shared/services/util.service';
import { AlertService } from '../../shared/services/alert.service';
import { DealerState } from 'src/app/modules/store/dealer/dealer.reducer';
import { StateDetails } from 'src/app/modules/shared/models/state';
import { selectStates } from 'src/app/modules/store/dealer/dealer.selectors';
import { emailValidator, phoneValidator } from 'src/app/modules/shared/validators/custom-email-validator';

@Component({
  selector: 'app-search-customer',
  templateUrl: './search-customer.component.html',
  styleUrls: ['./search-customer.component.scss']
})
export class SearchCustomerComponent implements OnInit, OnDestroy {

  isLoading$:Observable<boolean>;
  customers$:Observable<ICustomer[]>;
  searchCustomersForm: UntypedFormGroup;
  isEditMode: boolean=false;
  subscriptions: SubscriptionList = {};
  states$: Observable<StateDetails[]>;
  filterOptions:any = [
    {
        "filterId": "phone",
        "filterName": "Phone"
    },
    {
        "filterId": "email",
        "filterName": "Email"
    },
    {
        "filterId": "vin",
        "filterName": "VIN"
    }
  ]
  filterOptionSelected: SearchInputFilterType = 'phone';
  stateOptionSelected;
  statesList;
  filteredStatesList;
  preferredCountries = this.translate.currentLang ? this.translate.currentLang.split('-')[1].toLowerCase(): 'us';
  selectedOption = 'searchCustomer.phone';
  selectedOptionErrMsg = 'searchCustomer.pleaseEnterValidPhone';
  isLicenseToVinEnabled = getLicenseToVinEnabled();
  matcher = new DirtyErrorStateMatcher();

  constructor(private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly searchCustomerStore:Store<SearchCustomersState>,
    private readonly vehicleService:VehicleService,
    private readonly alertService: AlertService,
    private readonly dealerState: Store<DealerState>,
    private readonly translate: TranslateService,) { }

  ngOnInit(): void {
    this.isLoading$ = this.searchCustomerStore.select(fromSearchCustomerSelectors.selectCustomersLoading);
    const states$ = this.dealerState.select(selectStates);
    const safeStates$ = isObservable(states$)
      ? states$
      : of(states$);
    safeStates$
      .pipe(
        filter((states): states is StateDetails[] => Array.isArray(states))
      )
      .subscribe(states => {
        this.statesList = states;
        this.filteredStatesList = states;
      });
    
    this.searchCustomersForm = this.formBuilder.group({
      firstName: [''],
      lastName: [''],
      query:['', [Validators.required]],
      state: ['']
    })
    this.subscriptions.query = this.activatedRoute.queryParams.subscribe(params => {
      if (params.fn && params.ln && params.email) 
      { 
        this.searchCustomersForm.get('firstName').setValue(params.fn);
        this.searchCustomersForm.get('lastName').setValue(params.ln);
        this.searchCustomersForm.get('query').setValue(params.email);
      }
      else {
        this.searchCustomersForm.controls.query.setValidators([Validators.required, phoneValidator])
        this.searchCustomersForm.updateValueAndValidity();
      }
    });
    if(this.isLicenseToVinEnabled){
      this.filterOptions.push({
        "filterId": "licensePlate",
        "filterName": "License Plate"
      })
    }
  }

  hasError(controlName: string, errorName: string){
    return this.searchCustomersForm.get(controlName).hasError(errorName);
  }

  isValidForm() {
    const query = this.searchCustomersForm.get('query').value;
    const state = this.searchCustomersForm.get('state').value;
    if (query.includes('@') || query.length === 17) {
      return true;
    }
    else if ((query.length > 10 && query.slice(-10).match(/^\d+$/)) || (query.length === 10 && query.match(/^\d+$/)) || (state && query.length > 2 && query.length < 10)) {
      return true;
    }
    else {
      this.alertService.showAlert(this.selectedOptionErrMsg);
      return false;
    }
  }

  submitForm(){
    if(this.searchCustomersForm.valid && this.isValidForm()){
      const firstName = this.searchCustomersForm.get('firstName').value;
      const lastName = this.searchCustomersForm.get('lastName').value;
      const query = this.searchCustomersForm.get('query').value;
      const state = this.searchCustomersForm.get('state').value;

      if (this.filterOptionSelected === 'licensePlate') {
        this.vehicleService.searchByLicensePlate(query, state).pipe(
          timeout(10000),
          take(1),
          map(response => response?.data?.[0]?.vin || ''),
          catchError(() => of(''))
        ).subscribe((resolvedVin: string) => {
          this.dispatchSearchAndNavigate(firstName, lastName, query, state, resolvedVin);
        });
      } else {
        this.searchCustomerStore.dispatch(new fromSearchCustomersActions.SetPendingSearchInput({
          pendingSearchInput: this.getPendingSearchInput({ firstName, lastName, query, state })
        }));
        this.vehicleService.clearCurrentCustomerTelematics();
        this.searchCustomerStore.dispatch(new fromSearchCustomersActions.SearchCustomers({
          firstName,
          lastName,
          query: state ? `${query},${state}` : query
        }));
        this.router.navigate(['/asp/bdc/customer','list']);
      }
    }
  }

  private dispatchSearchAndNavigate(firstName: string, lastName: string, licensePlate: string, state: string, resolvedVin: string) {
    const vin = (resolvedVin || '').trim();
    const pendingSearchInput = vin
      ? this.getPendingSearchInput({ firstName, lastName, query: vin, state: '' }, 'vin')
      : this.getPendingSearchInput({ firstName, lastName, query: '', state }, 'vin');

    this.searchCustomerStore.dispatch(new fromSearchCustomersActions.SetPendingSearchInput({
      pendingSearchInput
    }));
    this.vehicleService.clearCurrentCustomerTelematics();
    this.searchCustomerStore.dispatch(new fromSearchCustomersActions.SearchCustomers({
      firstName,
      lastName,
      query: `${licensePlate},${state}`
    }));
    this.router.navigate(['/asp/bdc/customer', 'list']);
  }

  private getPendingSearchInput(params: {
    firstName: string,
    lastName: string,
    query: string,
    state: string
  }, filterTypeOverride?: SearchInputFilterType): PendingSearchInput {
    const pendingInput: PendingSearchInput = {
      firstName: params.firstName || '',
      lastName: params.lastName || '',
      filterType: filterTypeOverride || this.filterOptionSelected,
      query: params.query || '',
      stateCode: params.state || '',
      createdAt: new Date().toISOString()
    };

    return pendingInput;
  }

  vinValidator(): ValidatorFn {
    return (_control: UntypedFormControl): ValidationErrors => {
      const vin = this.searchCustomersForm.controls.query;
      if (!vin.value) {
        return { required: 'service.vinIsRequired' };
      } 
      if (!/^([a-zA-Z0-9]{17})$/.test(vin.value)) {
        return { pattern17DigitAndAlphaNumeric: 'service.vinShouldBeDigitAndAlphanumeric' };
      } else if (!/^(.{17})$/.test(vin.value)) {
        return { pattern17Digit: 'service.vinShouldBeDigit' };
      } else if (!/^([a-zA-Z0-9]*)$/.test(vin.value)) {
        return { patternAlphaNumeric: 'service.vinShouldBeAlphanumeric' };
      }
      return null;
    }
  }


  onSelectingFilterOption(value: SearchInputFilterType){
    this.filterOptionSelected = value;
    this.searchCustomersForm.patchValue({ query : '' });
    this.searchCustomersForm.controls.query.setValidators([Validators.required]);
    this.searchCustomersForm.controls.query.markAsUntouched();
    this.searchCustomersForm.controls.query.setErrors(null);
    this.selectedOption = 'searchCustomer.'+value;
    this.selectedOptionErrMsg = "searchCustomer.pleaseEnterValid"+value;
    if(value === 'email'){
      this.searchCustomersForm.controls.query.setValidators([emailValidator])
    }else if(value === 'licensePlate'){
      this.searchCustomersForm.controls.state.setValidators([Validators.required])
      this.searchCustomersForm.updateValueAndValidity();
    }else if(value === 'vin'){
      this.searchCustomersForm.controls.query.setValidators(this.vinValidator())
      this.searchCustomersForm.updateValueAndValidity();
    }else if(value === 'phone'){
      this.searchCustomersForm.controls.query.setValidators([phoneValidator])
      this.searchCustomersForm.updateValueAndValidity();
    }
    else {
      this.searchCustomersForm.controls.state.patchValue('');
      this.searchCustomersForm.controls.state.setValidators([]);
      this.searchCustomersForm.controls.state.setErrors(null);
      this.searchCustomersForm.updateValueAndValidity();
    }
  }

  filter(value): void {
    const filterValue = value.toLowerCase();
    this.filteredStatesList = this.statesList.filter(o => o.name.toLowerCase().includes(filterValue));
  }

  displayStateName = (abbrev: string) => {
     if (!abbrev){
       return '';
     } 
     const state = this.statesList.find(s => s.abbreviation === abbrev);
     return state ? state.name : '';
  };

  ngOnDestroy(): void {
    unsubscribeSubscriptions(this.subscriptions);
  }
}
